import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  FileText,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { LIQUIDATION_STATUS_BADGE_VARIANT } from "@/components/payroll/liquidation-format";
import { SettlementForm } from "@/components/payroll/SettlementForm";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import {
  useConfirmSettlement,
  useDiscardSettlement,
  useSettlement,
  useVoidSettlement,
} from "@/lib/api/payroll-settlements.queries";
import { usePayrollEmployee } from "@/lib/api/payroll-employees.queries";
import { translateApiError } from "@/lib/translate-api-error";
import { translateDraftError } from "@/lib/draft-errors";
import { useTeamPermission } from "@/lib/team-context";
import { useActiveCompany } from "@/lib/company-context";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import type {
  SettlementBreakdown,
  SettlementConcept,
} from "@/lib/api/payroll-settlements";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/definitivas/$settlementId",
)({
  component: SettlementDetailPage,
});

function SettlementDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SettlementDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function SettlementDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, settlementId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const detailQuery = useSettlement(companyId, settlementId);
  const confirmMutation = useConfirmSettlement(companyId);
  const voidMutation = useVoidSettlement(companyId);
  const discardMutation = useDiscardSettlement(companyId);
  const canWrite = useTeamPermission("payroll.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const employeeQuery = usePayrollEmployee(
    companyId,
    detailQuery.data?.employeeId,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);

  const breakdown = useMemo(
    () => (detailQuery.data?.detail ?? {}) as Partial<SettlementBreakdown>,
    [detailQuery.data],
  );

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("settlements.detail.error_title")}
        description={t("settlements.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const settlement = detailQuery.data;
  // Only a draft can be reopened; once confirmed the figures (and their entry) are final (PF-463).
  const canEdit = canWrite && settlement.editable;
  const canDiscard = canWrite && settlement.status === "draft";
  const employee = employeeQuery.data;
  const concepts = breakdown.concepts;

  async function handleConfirm() {
    try {
      // Confirm exactly the revision on screen: if someone saved since, the server refuses (409 DRAFT_STALE).
      await confirmMutation.mutateAsync({ settlementId, expectedRevision: settlement.revision });
      toast.success(t("settlements.toast.confirmed"));
      setConfirmOpen(false);
    } catch (err) {
      toast.error(translateDraftError(err, t) ?? translateApiError(err, t, "settlements.error"));
      setConfirmOpen(false);
    }
  }

  async function handleDiscard() {
    try {
      await discardMutation.mutateAsync({ settlementId, expectedRevision: settlement.revision });
      toast.success(t("settlements.toast.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/nomina/definitivas", params: { companyId } });
    } catch (err) {
      toast.error(translateDraftError(err, t) ?? translateApiError(err, t, "settlements.error"));
      setDiscardOpen(false);
    }
  }

  async function handleVoid() {
    try {
      await voidMutation.mutateAsync({ settlementId });
      toast.success(t("settlements.toast.voided"));
      setVoidOpen(false);
    } catch (err) {
      toast.error(translateApiError(err, t, "settlements.error"));
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina/definitivas"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("settlements.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {employee
                ? `${employee.firstName} ${employee.lastName}`
                : t("settlements.detail.title")}
            </h1>
            <Badge variant={LIQUIDATION_STATUS_BADGE_VARIANT[settlement.status]}>
              {t(`liquidations.status.${settlement.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("settlements.detail.dates", {
              hire: settlement.hireDate,
              termination: settlement.terminationDate,
            })}{" "}
            · {t(`settlements.reason.${settlement.terminationReason}`)}
            {breakdown.daysWorkedTotal !== undefined
              ? ` · ${t("settlements.detail.days_worked", { days: breakdown.daysWorkedTotal })}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("settlements.actions.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discardMutation.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("settlements.actions.discard")}
            </Button>
          ) : null}
          {settlement.status === "draft" && !editing ? (
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={confirmMutation.isPending}
            >
              <CheckCircle2 className="h-4 w-4" />
              {t("settlements.actions.confirm")}
            </Button>
          ) : null}
          {settlement.status !== "void" && !editing ? (
            <Button
              variant="destructive"
              onClick={() => setVoidOpen(true)}
              disabled={voidMutation.isPending}
            >
              <Ban className="h-4 w-4" />
              {t("settlements.actions.void")}
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <SettlementForm
          companyId={companyId}
          initialSettlement={settlement}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
      <>
      {/* Headline total */}
      <Card className="border-emerald-200 dark:border-emerald-900/40">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("settlements.detail.total")}
            </p>
            <p className="mt-1 font-mono text-3xl font-semibold text-emerald-700 dark:text-emerald-300">
              {formatCop(settlement.totalAmount)}
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <p>
              {t("settlements.detail.base_salary")}:{" "}
              <span className="font-mono">
                {formatCop(settlement.baseSalary)}
              </span>
            </p>
            {breakdown.vacationDays ? (
              <p>
                {t("settlements.detail.vacation_days", {
                  days: breakdown.vacationDays.used,
                })}{" "}
                (
                {breakdown.vacationDays.source === "explicit"
                  ? t("settlements.detail.vacation_explicit")
                  : t("settlements.detail.vacation_computed")}
                )
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Per-concept cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {concepts?.salariosPendientes ? (
          <ConceptCard
            title={t("settlements.concept.salarios")}
            concept={concepts.salariosPendientes}
            extra={
              concepts.salariosPendientes.transportPortion > 0
                ? t("settlements.concept.salarios_split", {
                    salary: formatCop(
                      concepts.salariosPendientes.salaryPortion,
                    ),
                    transport: formatCop(
                      concepts.salariosPendientes.transportPortion,
                    ),
                  })
                : undefined
            }
          />
        ) : null}
        {concepts?.cesantias ? (
          <ConceptCard
            title={t("settlements.concept.cesantias")}
            concept={concepts.cesantias}
            consumed={settlement.consumedProvisionCesantias}
          />
        ) : null}
        {concepts?.interesesCesantias ? (
          <ConceptCard
            title={t("settlements.concept.intereses")}
            concept={concepts.interesesCesantias}
            consumed={settlement.consumedProvisionIntereses}
          />
        ) : null}
        {concepts?.prima ? (
          <ConceptCard
            title={t("settlements.concept.prima")}
            concept={concepts.prima}
            consumed={settlement.consumedProvisionPrima}
          />
        ) : null}
        {concepts?.vacaciones ? (
          <ConceptCard
            title={t("settlements.concept.vacaciones")}
            concept={concepts.vacaciones}
            consumed={settlement.consumedProvisionVacaciones}
          />
        ) : null}
        {concepts?.indemnizacion ? (
          <ConceptCard
            title={t("settlements.concept.indemnizacion")}
            concept={concepts.indemnizacion}
            extra={
              concepts.indemnizacion.tier !== "none"
                ? t(`settlements.tier.${concepts.indemnizacion.tier}`)
                : undefined
            }
          />
        ) : null}
      </div>

      {!concepts ? (
        /* Fallback when detail_json is unreadable — summary amounts only. */
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("settlements.detail.amounts")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label={t("settlements.concept.salarios")}
              value={formatCop(settlement.pendingSalaryAmount)}
            />
            <Row
              label={t("settlements.concept.cesantias")}
              value={formatCop(settlement.cesantiasAmount)}
            />
            <Row
              label={t("settlements.concept.intereses")}
              value={formatCop(settlement.interesesCesantiasAmount)}
            />
            <Row
              label={t("settlements.concept.prima")}
              value={formatCop(settlement.primaAmount)}
            />
            <Row
              label={t("settlements.concept.vacaciones")}
              value={formatCop(settlement.vacacionesAmount)}
            />
            <Row
              label={t("settlements.concept.indemnizacion")}
              value={formatCop(settlement.indemnizacionAmount)}
            />
          </CardContent>
        </Card>
      ) : null}

      {settlement.journalEntryId ? (
        <Card>
          <CardContent className="pt-6 text-sm">
            <Link
              to="/companies/$companyId/contabilidad/diario/$entryId"
              params={{ companyId, entryId: settlement.journalEntryId }}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <FileText className="h-4 w-4" />
              {t("settlements.detail.journal_link")}
            </Link>
          </CardContent>
        </Card>
      ) : null}

      {settlement.notes ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("settlements.detail.notes")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">{settlement.notes}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="payroll_settlements"
            documentId={settlementId}
          />
        </CardContent>
      </Card>

      </>
      )}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("settlements.discard.title")}
        description={t("settlements.discard.body")}
        pending={discardMutation.isPending}
        onConfirm={() => void handleDiscard()}
      />

      {/* Confirm dialog — warns about employee termination */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settlements.confirm.title")}</DialogTitle>
            <DialogDescription>
              {t("settlements.confirm.body", {
                name: employee
                  ? `${employee.firstName} ${employee.lastName}`
                  : "",
                total: formatCop(settlement.totalAmount),
              })}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
            {t("settlements.confirm.terminate_warning")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={confirmMutation.isPending}
            >
              {t("settlements.confirm.cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void dialog */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settlements.void.title")}</DialogTitle>
            <DialogDescription>
              {settlement.status === "confirmed"
                ? t("settlements.void.body_confirmed")
                : t("settlements.void.body_draft")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={voidMutation.isPending}
            >
              {t("settlements.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConceptCard({
  title,
  concept,
  consumed,
  extra,
}: {
  title: string;
  concept: SettlementConcept;
  consumed?: number;
  extra?: string;
}) {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{title}</CardTitle>
          <span className="font-mono text-base font-semibold">
            {formatCop(concept.amount)}
          </span>
        </div>
        {extra ? <CardDescription>{extra}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-1 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>{t("settlements.concept.base")}</span>
          <span className="font-mono">{formatCop(concept.base)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>{t("settlements.concept.days")}</span>
          <span className="font-mono">{concept.days}</span>
        </div>
        <p className="break-words border-t pt-1 font-mono text-[10px]">
          {concept.formula}
        </p>
        {consumed !== undefined && consumed > 0 ? (
          <p className="text-[10px]">
            {t("settlements.concept.consumed", {
              amount: formatCop(consumed),
            })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
